import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

/**
 * ReadOnlyTextRenderer - Uses the same TipTap renderer as EditableTextSection
 * but in a read-only, toolbar-less mode for admin preview.
 * This ensures admins see the exact same rendering as end users.
 */
const ReadOnlyTextRenderer = ({ content, placeholder = "Output will appear here..." }) => {
  const hasContent = content && content.trim() !== "";

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
    ],
    content: hasContent ? content : "",
    editable: false,
  });

  // Sync content when prop changes
  React.useEffect(() => {
    if (editor && content) {
      const currentHTML = editor.getHTML();
      if (content !== currentHTML) {
        editor.commands.setContent(content, false);
      }
    }
  }, [editor, content]);

  return (
    <Box
      className="text-editor-container"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        minHeight: 120,
        maxHeight: 400,
        overflowY: 'auto',
        bgcolor: 'background.paper',
        // Match EditableTextSection editor styling
        '& .tiptap': {
          padding: '12px',
          border: 'none',
          outline: 'none',
          fontSize: 'var(--text-base)',
        },
        '& .tiptap p': {
          marginTop: 0,
          marginBottom: '1em',
        },
        '& .tiptap p:last-child': {
          marginBottom: 0,
        },
      }}
    >
      {hasContent ? (
        <div className="text-editor-box text-readonly">
          <EditorContent editor={editor} style={{ height: "100%" }} />
        </div>
      ) : (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {placeholder}
        </Typography>
      )}
    </Box>
  );
};

export default ReadOnlyTextRenderer;
